'use client';

import {
  Button,
  Icon,
  Menu,
  MenuContent,
  MenuDivider,
  MenuItem,
  MenuSection,
  MenuSub,
  MenuSubContent,
  MenuSubTrigger,
  MenuTrigger,
} from '@lulalife/design-system';
import { DotsVertical } from '@lulalife/design-system/icons/line';
import { type DragEvent, useId, useState } from 'react';
// DS-GAP(menu-item-selected-focus): see ListViewsMenu.
import { SELECTED_ITEM_CLASS_NAME } from './ListViewsMenu';
import { MenuScrollRegion } from './MenuScrollRegion';

/** Table Options · Open (8228:4880), Group By (7633:180691, 7506:10715) and Manage Columns (7633:180835). */
const OPTIONS_WIDTH = 220;
const GROUP_BY_WIDTH = 240;
const COLUMNS_WIDTH = 260;
/** Work Orders Manage Columns (7506:10414). */
const WORK_ORDER_COLUMNS_WIDTH = 240;

export type ListGroupByOption = { value: string; label: string; isDefault: boolean };
export type ListColumnOption = { id: string; label: string; checked: boolean; disabled: boolean };

type GroupByProps = {
  options: ReadonlyArray<ListGroupByOption>;
  selected: string;
  onChange: (value: string) => void;
};

/** Service Requests (7633:180835): locked columns first, then the optional ones with drag handles. */
type GroupedColumnsProps = {
  /** Checked and disabled, drawn first. */
  locked: ReadonlyArray<ListColumnOption>;
  /** Can be hidden and dragged into a new order. */
  optional: ReadonlyArray<ListColumnOption>;
  onToggle: (id: string) => void;
  /** The optional columns' ids in their new order. */
  onReorder: (ids: string[]) => void;
};

/**
 * Work Orders (7506:10414): one list of checkboxes in the table's order, no handles and no locked
 * styling. A `disabled` column is still drawn like the rest but stays on.
 */
type FlatColumnsProps = {
  all: ReadonlyArray<ListColumnOption>;
  onToggle: (id: string) => void;
};

type ListTableOptionsMenuProps =
  | { variant?: 'service-requests'; groupBy: GroupByProps; columns: GroupedColumnsProps }
  | { variant: 'work-orders'; groupBy: GroupByProps; columns: FlatColumnsProps };

const moveId = (ids: string[], id: string, to: number) => {
  const next = ids.filter((item) => item !== id);
  next.splice(to, 0, id);
  return next;
};

/** The optional columns, each with a drag handle (drag, or arrow keys on the handle). */
const OptionalColumns = ({ columns }: { columns: GroupedColumnsProps }) => {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const hintId = useId();
  const ids = columns.optional.map((column) => column.id);

  const drop = (targetId: string, event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const draggedId = event.dataTransfer.getData('text/plain');
    if (draggedId && draggedId !== targetId && ids.includes(draggedId)) {
      columns.onReorder(moveId(ids, draggedId, ids.indexOf(targetId)));
    }
    setDragId(null);
    setOverId(null);
  };

  return (
    <>
      <span id={hintId} className="sr-only">
        Use arrow up or arrow down to move this column.
      </span>
      {columns.optional.map((column, index) => (
        <MenuItem
          key={column.id}
          type="checkbox"
          label={column.label}
          checked={column.checked}
          disabled={column.disabled}
          onCheckedChange={() => columns.onToggle(column.id)}
          className={
            dragId === column.id
              ? 'opacity-50'
              : overId === column.id
                ? 'bg-menu-item-bg-hover'
                : undefined
          }
          reorderHandle={{
            ariaDescribedBy: hintId,
            onMove: (direction) => {
              const to = index + direction;
              if (to >= 0 && to < ids.length) columns.onReorder(moveId(ids, column.id, to));
            },
            onDragStart: (event) => {
              setDragId(column.id);
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', column.id);
            },
            onDragEnd: () => {
              setDragId(null);
              setOverId(null);
            },
            onDragEnter: (event) => event.preventDefault(),
            onDragOver: (event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              if (overId !== column.id) setOverId(column.id);
            },
            onDragLeave: () => {
              if (overId === column.id) setOverId(null);
            },
            onDrop: (event) => drop(column.id, event),
          }}
        />
      ))}
    </>
  );
};

/** Group By (SR 7633:180691): "GROUP BY", a divider, plain rows with "(default)" on the default. */
const ServiceRequestGroupBy = ({
  groupBy,
  onPicked,
}: {
  groupBy: GroupByProps;
  onPicked: () => void;
}) => (
  <MenuSubContent width={GROUP_BY_WIDTH} className="w-[240px]!">
    <MenuSection label="GROUP BY" />
    <MenuDivider />
    <div className="flex flex-col">
      {groupBy.options.map((option) => (
        <MenuItem
          key={option.value}
          label={option.label}
          rightText={option.isDefault ? '(default)' : undefined}
          selected={option.value === groupBy.selected}
          className={option.value === groupBy.selected ? SELECTED_ITEM_CLASS_NAME : undefined}
          onClick={() => {
            onPicked();
            if (option.value !== groupBy.selected) groupBy.onChange(option.value);
          }}
        />
      ))}
    </div>
  </MenuSubContent>
);

/** Group By (WO 7506:10715): "Group By" and radio rows, the default reading "None (default)". */
const WorkOrderGroupBy = ({
  groupBy,
  onPicked,
}: {
  groupBy: GroupByProps;
  onPicked: () => void;
}) => (
  <MenuSubContent width={GROUP_BY_WIDTH} className="w-[240px]!">
    <MenuSection label="Group By" />
    <div className="flex flex-col">
      {groupBy.options.map((option) => (
        <MenuItem
          key={option.value}
          type="radio"
          label={option.isDefault ? `${option.label} (default)` : option.label}
          checked={option.value === groupBy.selected}
          onClick={() => {
            onPicked();
            if (option.value !== groupBy.selected) groupBy.onChange(option.value);
          }}
        />
      ))}
    </div>
  </MenuSubContent>
);

/** Manage Columns (SR 7633:180835): "MANAGE COLUMNS", the locked columns, then the optional ones. */
const ServiceRequestColumns = ({ columns }: { columns: GroupedColumnsProps }) => (
  <MenuSubContent width={COLUMNS_WIDTH} className="w-[260px]!">
    <MenuSection label="MANAGE COLUMNS" />
    <MenuDivider />
    <div className="flex flex-col">
      {columns.locked.map((column) => (
        <MenuItem
          key={column.id}
          type="checkbox"
          label={column.label}
          checked={column.checked}
          disabled
        />
      ))}
    </div>
    <MenuDivider />
    <div className="flex flex-col">
      <OptionalColumns columns={columns} />
    </div>
  </MenuSubContent>
);

/**
 * Manage Columns (WO 7506:10414): "Manage Columns" and every column as a checkbox, as drawn. The
 * annotation keeps locked columns on, but the frame draws them like the rest, so a locked row
 * looks the same and ignores the click.
 */
const WorkOrderColumns = ({ columns }: { columns: FlatColumnsProps }) => (
  <MenuSubContent width={WORK_ORDER_COLUMNS_WIDTH} className="w-[240px]!">
    <MenuSection label="Manage Columns" />
    {/* 15 rows (~590): on a short window the list scrolls rather than run off the screen. */}
    <MenuScrollRegion>
      {columns.all.map((column) => (
        <MenuItem
          key={column.id}
          type="checkbox"
          label={column.label}
          checked={column.checked}
          onCheckedChange={() => {
            if (!column.disabled) columns.onToggle(column.id);
          }}
        />
      ))}
    </MenuScrollRegion>
  </MenuSubContent>
);

/**
 * The ⋮ table options (Service Requests 8228:4880, Work Orders 8234:16542): "Group by" and "Manage
 * columns", each opening its menu to the side. `variant` picks the list's frames for the two menus.
 *
 * DS-GAP(table-options-menu): the package's `TableOptionsMenu` labels its row "Columns", draws
 * leading icons, no section dividers and a 224 panel; Figma has "Manage columns", section headers
 * (with dividers on Service Requests) and 220/240/260 panels, so this is built from the DS `Menu`
 * parts. Its drag handle is the DS `MenuItem` one (dots-grid), not Figma's menu-01.
 */
export const ListTableOptionsMenu = (props: ListTableOptionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);
  return (
    <Menu open={isOpen} onOpenChange={setIsOpen}>
      <MenuTrigger>
        <Button
          type="button"
          variant="secondary"
          iconOnly
          aria-label="Table options"
          data-testid="table-options"
        >
          <Icon size="sm" aria-hidden="true">
            <DotsVertical width="100%" height="100%" strokeWidth={1.5} />
          </Icon>
        </Button>
      </MenuTrigger>
      <MenuContent data-lula-ds="" align="end" width={OPTIONS_WIDTH} aria-label="Table options">
        <MenuSub>
          <MenuSubTrigger label="Group by" />
          {props.variant === 'work-orders' ? (
            <WorkOrderGroupBy groupBy={props.groupBy} onPicked={close} />
          ) : (
            <ServiceRequestGroupBy groupBy={props.groupBy} onPicked={close} />
          )}
        </MenuSub>
        <MenuSub>
          <MenuSubTrigger label="Manage columns" />
          {props.variant === 'work-orders' ? (
            <WorkOrderColumns columns={props.columns} />
          ) : (
            <ServiceRequestColumns columns={props.columns} />
          )}
        </MenuSub>
      </MenuContent>
    </Menu>
  );
};
